import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";
import { useLanguage } from "../contexts/LanguageContext";
import Logo from "../components/Logo";
import { useMutation } from "@tanstack/react-query";
import { updateUser } from "../api/mutations";
import LanguageSelector from "../components/LanguageSelector";
import { getFirebaseErrorMessage } from "../utils/firebaseErrorHandler";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState({
    googleFailed: "",
    email: "",
    password: "",
    generic: "",
    tos: "",
  });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const authPendingRef = useRef(false);
  const { t } = useLanguage();

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Clear password error if password becomes valid
    if (error.password && newPassword.length >= 6) {
      setError((prev) => ({ ...prev, password: "" }));
    }
  };

  const updateUserMutation = useMutation({
    mutationFn: updateUser,
  });

  const handleAffiliate = async () => {
    const promotekitId = window.promotekit_referral;
    if (promotekitId) {
      try {
        await updateUserMutation.mutateAsync({
          data: {
            promotekit_referral_id: promotekitId,
          },
        });
      } catch (error) {
        console.error("Failed to update promotekit ID:", error);
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const handleSubmit = async (e) => {
    // preventDefault before the guard, or a blocked submit falls through to a
    // native form post.
    e.preventDefault();
    if (authPendingRef.current) return;
    authPendingRef.current = true;
    setError({
      googleFailed: "",
      email: "",
      password: "",
      generic: "",
      tos: "",
    });

    try {
      setIsLoading(true);
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      if (name) {
        await updateProfile(result.user, {
          displayName: name,
        });
      }
      await result.user.getIdToken();
      await handleAffiliate();
      navigate("/dashboard");
    } catch (error) {
      authPendingRef.current = false;
      setIsLoading(false);
      const message = getFirebaseErrorMessage(error, t);
      if (error.code === "auth/email-already-in-use") {
        setError((prev) => ({ ...prev, email: message }));
      } else if (
        error.code === "auth/weak-password" ||
        error.code === "auth/missing-password"
      ) {
        setError((prev) => ({ ...prev, password: message }));
      } else if (error.code === "auth/invalid-email") {
        setError((prev) => ({ ...prev, email: message }));
      } else {
        setError((prev) => ({ ...prev, generic: message }));
      }
    }
  };

  const handleGoogleSignIn = async () => {
    // `isLoading` is a render-time value, so two clicks dispatched before React
    // re-renders both read false — as does the disabled attribute, which lands a
    // render later. A ref updates synchronously and closes that window. Firebase
    // corrupts its own promise bookkeeping if a second popup starts while one is
    // pending ("INTERNAL ASSERTION FAILED: Pending promise was never set").
    if (authPendingRef.current) return;
    authPendingRef.current = true;
    setError({
      googleFailed: "",
      email: "",
      password: "",
      generic: "",
    });
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      await result.user.getIdToken();

      await handleAffiliate();
      navigate("/dashboard");
    } catch (error) {
      authPendingRef.current = false;
      setIsLoading(false);
      if (error?.code === "auth/popup-closed-by-user") return;
      setError((prev) => ({
        ...prev,
        googleFailed: getFirebaseErrorMessage(error, t),
      }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          {t("auth.signup.title")}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t("auth.signup.haveAccount")}{" "}
          <Link
            to="/login"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            {t("auth.signup.signInLink")}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white p-10 shadow-lg sm:rounded-lg">
          {error.googleFailed && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error.googleFailed}</div>
            </div>
          )}
          <div className="mb-6">
            <div>
              <button
                id="google-signup-button"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className={`flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                  setError ? "ring-red-300" : "ring-gray-300"
                } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {t("auth.signup.signUpWithGoogle")}
              </button>
            </div>

            <div className="relative mt-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">
                  {t("auth.signup.continueWith")}
                </span>
              </div>
            </div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error.generic && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error.generic}</div>
              </div>
            )}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                {t("auth.signup.fullNameLabel")}
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm ${
                    error.generic ? "ring-red-300" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}

                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                {t("auth.signup.emailLabel")}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm ${
                    error.email ? "ring-red-300" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}

                />
                {error.email && (
                  <p className="mt-2 text-sm text-red-600">{error.email}</p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                {t("auth.signup.passwordLabel")}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  className={`block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm ${
                    error.password ? "ring-red-300" : "ring-gray-300"
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                />
                {error.password && (
                  <p className="mt-2 text-sm text-red-600">{error.password}</p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-start">
                <div className="">
                  <label htmlFor="tos" className="text-xs text-gray-500">
                    {t("auth.signup.agreedTosAgreement", {
                      termsOfUseLink: (
                        <a
                          href="https://www.loyaltify.io/terms-of-use/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {t("auth.signup.termsOfUse")}
                        </a>
                      ),
                      privacyPolicyLink: (
                        <a
                          href="https://www.loyaltify.io/privacy-policy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {t("auth.signup.privacyPolicy")}
                        </a>
                      ),
                    })}
                  </label>
                </div>
              </div>
              {error.tos && (
                <p className="mt-2 text-sm text-red-600">{error.tos}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                id="signup-button"
                disabled={isLoading}
                className={`
                  flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-xs hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                  ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                `}
              >
                {isLoading
                  ? t("common.loading")
                  : t("auth.signup.signUpButton")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
